import { twMerge } from '@shared/themes';
import { NumberFormats, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import type { CheckoutCartSummary, CheckoutTicketDetail, CheckoutTicketDetailType } from '../../../../types';

type CheckoutSummaryProps = {
  cartSummary: CheckoutCartSummary;
  ticketDetails: CheckoutTicketDetail[];
  addToBalance?: number;
};

function getTicketDetailRowClassName(type: CheckoutTicketDetailType): string {
  if (type === 'DISCOUNT') {
    return 'text-success-foreground';
  }

  return '';
}

function CheckoutSummary({ cartSummary, ticketDetails, addToBalance }: CheckoutSummaryProps) {
  const { payable, bonusDiscount, usedBalance } = cartSummary;
  const t = useTranslations('global_sheets.checkout');
  const currencyFormatter = useCurrencyFormatter();

  return (
    <div className="flex flex-col">
      <div className="rounded-md bg-container p-5 flex flex-col gap-y-2" data-testid="checkout-ticket-summary">
        {ticketDetails.map((detail, index) => (
          <div
            key={`${detail.text}-${index}`}
            className={twMerge('flex justify-between items-center', getTicketDetailRowClassName(detail.type))}
            data-testid={`checkout-ticket-detail-${index}`}
          >
            <span className="paragraph-s">{detail.text}</span>
            <span className="paragraph-s">{detail.price}</span>
          </div>
        ))}

        {usedBalance > 0 && payable > 0 && (
          <div className="flex justify-between items-center" data-testid="checkout-balance-row">
            <span className="paragraph-s">{t('summary.taken_from_balance')}</span>
            <span className="paragraph-s">-{currencyFormatter(usedBalance).format(NumberFormats.WITH_DECIMALS).get()}</span>
          </div>
        )}

        {bonusDiscount > 0 && (
          <div className="flex justify-between items-center text-success-foreground" data-testid="checkout-voucher-row">
            <span className="paragraph-s">{t('summary.voucher')}</span>
            <span className="paragraph-s">-{currencyFormatter(bonusDiscount).format(NumberFormats.WITH_DECIMALS).get()}</span>
          </div>
        )}

        <div className="flex justify-between items-center" data-testid="checkout-total-row">
          <span className="paragraph-strong">{t('summary.total')}</span>
          <span className="paragraph-strong">
            {currencyFormatter(payable === 0 ? usedBalance : payable)
              .format(NumberFormats.WITH_DECIMALS)
              .get()}
          </span>
        </div>

        {addToBalance !== undefined && (
          <div className="flex justify-between items-center" data-testid="checkout-add-to-balance-row">
            <span className="paragraph-s">{t('summary.add_to_balance')}</span>
            <span className="paragraph-s">+{currencyFormatter(addToBalance).format(NumberFormats.WITH_DECIMALS).get()}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export { CheckoutSummary, type CheckoutSummaryProps };
